import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router";
import { useSelector, useDispatch } from "react-redux";
import { useFormik } from "formik";
import { useToast } from "../../Hooks/useToast";

import Box from "@mui/material/Box";
import Divider from "@mui/material/Divider";
import Typography from "@mui/material/Typography";

import AlternativeHeader from "../../Components/Headres/Alternative_Header";
import NotesModal from "../Reservations/Add_Notes_Modal";
import UsersCard from "../../Components/Cards/Users_Card";
import DatagridTable from "../../Components/Table/DataGrid_Table";
import CustomButton from "../../Components/Utils/Custom_Btn";
import { createColumn } from "../../Components/Table/DataGrid_Cullums";

import { fetchgetUserById } from "../../Redux/Users_Slice";
import { fetchReservationsByUser } from "../../Redux/Reservations_Slice";
import { updateUser } from "../../Apis/Users/Users";

const RATING_VALUES = {
  GOOD: "Grüne Karte",
  BAD: "Rote Karte"
};

const ViewUsers = () => {
  const param = useParams();
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { toastSuccess, toastError } = useToast();
  const [modalOpen, setModalOpen] = useState(false);
  const [adminNote, setAdminNotes] = useState("");
  const [loading, setIsLoading] = useState(false);
  const [paginationModel, setPaginationModel] = useState({
    page: 0,
    pageSize: 10,
  });

  const UsersData = useSelector((state) => state.Users);
  const { data, isLoading, pagination } = useSelector(
    (state) => state.Reservations
  );

  useEffect(() => {
    if (UsersData?.data?.data?.adminNote) {
      setAdminNotes(UsersData.data.data.adminNote);
    }
  }, [UsersData]);

  const formik = useFormik({
    initialValues: {
      isBadUser: UsersData?.data?.data?.isBadUser ? RATING_VALUES.BAD : RATING_VALUES.GOOD,
    },
    onSubmit: async (values) => {
      if (param.id) {
        setIsLoading(true);
        try {
          const boolValue = values.isBadUser === RATING_VALUES.BAD;
          const res = await updateUser(param.id, {
            isBadUser: boolValue,
            adminNote,
          });
          if (res.status === 200) {
            toastSuccess("Der Benutzer wurde erfolgreich aktualisiert.");
            navigate("/Kunden");
          }
        } catch (error) {
          toastError("Ein Fehler ist aufgetreten. Bitte versuchen Sie es erneut.");
        } finally {
          setIsLoading(false);
        }
      }
    },
    enableReinitialize: true,
  });

  useEffect(() => {
    if (param.id) {
      dispatch(fetchgetUserById(param.id));
    }
  }, [param.id, dispatch]);

  useEffect(() => {
    if (param.id) {
      dispatch(
        fetchReservationsByUser({
          userId: param.id,
          page: paginationModel.page + 1,
          limit: paginationModel.pageSize,
        })
      );
    }
  }, [dispatch, paginationModel.page, paginationModel.pageSize, param.id]);

  const handlePaginationModelChange = (newModel) => {
    setPaginationModel(newModel);
  };

  const columns = [
    createColumn("reservationNumber", "Reservierungsnummer", 120),
    createColumn("numberOfPersons", "Anzahl der Personen", 120),
    {
      ...createColumn("dateOfReservation", "Datum der Reservierung", 200),
      renderCell: (params) => {
        const date = new Date(params.value);
        const day = String(date.getUTCDate()).padStart(2, '0');
        const month = String(date.getUTCMonth() + 1).padStart(2, '0');
        const year = date.getUTCFullYear();
        const hours = String(date.getUTCHours()).padStart(2, '0');
        const minutes = String(date.getUTCMinutes()).padStart(2, '0');
        return `${hours}:${minutes} ${day}.${month}.${year}`;
      }
    },
    {
      ...createColumn("dateEndOfReservation", "Datum Ende der Reservierung", 250),
      renderCell: (params) => {
        const date = new Date(params.value);
        const day = String(date.getUTCDate()).padStart(2, '0');
        const month = String(date.getUTCMonth() + 1).padStart(2, '0');
        const year = date.getUTCFullYear();
        const hours = String(date.getUTCHours()).padStart(2, '0');
        const minutes = String(date.getUTCMinutes()).padStart(2, '0');
        return `${hours}:${minutes} ${day}.${month}.${year}`;
      },
    },
    createColumn("payWay", "Zahlungsart", 120),
    createColumn("status", "Status", 200, "string", ({ value }) => {
      let color, label;
      if (value === "accepted" || value === "Angenommen") {
        color = "#B6E6C1";
        label = "Angenommen";
      } else if (value === "rejected" || value === "Abgelehnt") {
        color = "#E6B6B6";
        label = "Abgelehnt";
      } else {
        color = "#F0E0C0";
        label = "Ausstehend";
      }
      return (
        <span
          style={{
            backgroundColor: color,
            padding: "12px 18px",
            borderRadius: "10px",
          }}
        >
          {label}
        </span>
      );
    }),
    createColumn("isBadUser", "Bewertung", 150, "boolean", (params) => (
      <Box
        sx={{
          bgcolor: params.value ? "#C03C3C38" : "#36D06A38",
          borderRadius: "14px",
          minWidth: 110,
          display: "inline-block",
          py: "5px",
          border: `3px solid ${params.value ? "#C03C3C38" : "#36D06A38"}`,
        }}
      >
        <Typography
          sx={{
            fontSize: "16px",
            color: params.value ? "#C03C3C" : "#36D06A",
            textAlign: "center",
          }}
        >
          {params.value ? RATING_VALUES.BAD : RATING_VALUES.GOOD}
        </Typography>
      </Box>
    )),
    createColumn("isPaid", "Bezahlt", 120, "boolean"),
    createColumn("isCanceled", "Storniert", 120, "boolean"),
    createColumn("isAGift", "Geschenk", 120, "boolean"),
  ];

  return (
    <Box>
      <AlternativeHeader
        onPrevious={() => navigate("/Kunden")}
        title="Kundendatei"
      />
      {UsersData?.data?.data && (
        <UsersCard
          firstName={UsersData?.data?.data?.firstName}
          lastName={UsersData?.data?.data?.lastName}
          email={UsersData?.data?.data?.email}
          phoneNumber={UsersData?.data?.data?.phoneNumber}
          address={UsersData?.data?.data?.address}
          isBadUser={formik.values.isBadUser}
          numberOfReservations={UsersData?.data?.data?.numberOfReservations}
          isEditable={true}
          handleChangeRating={formik.handleChange}
          onClick={() => setModalOpen(true)}
        />
      )}
      <Divider sx={{ marginTop: 3, borderColor: "#000000" }} />
      <Box mt={2} display="flex" justifyContent="end">
        <CustomButton onNavigate={formik.handleSubmit} title="bearbeiten" />
      </Box>
      <Box sx={{ height: 300, width: "100%", mt: 4 }}>
        <DatagridTable
          columns={columns}
          rows={data || []}
          loading={isLoading}
          paginationModel={paginationModel}
          onPaginationModelChange={handlePaginationModelChange}
          rowCount={pagination?.total || 0}
          pageSizeOptions={[5, 10, 25]}
        />
      </Box>
      <NotesModal
        open={modalOpen}
        handleClose={() => setModalOpen(false)}
        notes={adminNote}
        setNotes={setAdminNotes}
        handleSave={async () => {
          setIsLoading(true);
          try {
            const res = await updateUser(param.id, { adminNote });
            if (res.status === 200) {
              toastSuccess("Die Notiz wurde erfolgreich gespeichert.");
              setModalOpen(false);
            }
          } catch (error) {
            toastError("Ein Fehler ist aufgetreten. Bitte versuchen Sie es erneut.");
          } finally {
            setIsLoading(false);
          }
        }}
        
      />
    </Box>
  );
};

export default ViewUsers;
